/**
 * @file Column metadata for current parent-board table views.
 */

/**
 * Column definitions for Patient table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_PATIENT_COLUMNS = Object.freeze({
  case_file_number: "Case File #",
  name: "Patient Name",
  case_manager: "Case Manager",
  provider: "Provider(s)",
  types: "Type",
  priority: "Urgency",
  status: "Status",
  case_value: "Case Value",
  next_action_required: "Next Action",
  next_appointment: "Next Appointment",
  first_seen: "First Seen",
});

/**
 * Column definitions for Project table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_PROJECT_COLUMNS = Object.freeze({
  name: "Project Name",
  project_manager: "Project Manager",
  types: "Type",
  priority: "Urgency",
  status: "Status",
  delivery_date: "Delivery Date",
  budget: "Budget",
  next_due_date: "Next Due Date",
});

/**
 * Column definitions for Asset table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_ASSET_COLUMNS = Object.freeze({
  name: "Asset Name",
  serial_number: "Serial #",
  types: "Type",
  equipment_status: "Status",
  next_service_due: "Next Service",
  purchase_date: "Purchase Date",
  purchase_price: "Purchase Price",
  supplier: "Supplier",
  warranty: "Warranty",
  installer: "Installer",
});

/**
 * Column definitions for Task table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_TASK_COLUMNS = Object.freeze({
  title: "Task Title",
  parent: "Linked To",
  owner: "Owner",
  assigned_users: "Assigned",
  types: "Type",
  priority: "Urgency",
  status: "Status",
  due_date: "Due Date",
});

/**
 * Column definitions for future Labs table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_LAB_COLUMNS = Object.freeze({
  patient_name: "Patient",
  stream: "Stream",
  lab: "Lab",
  types: "Type",
  priority: "Urgency",
  status: "Status",
  provider: "Provider",
  assigned_users: "Assigned",
  due_date: "Due Date",
  cost: "Cost",
});

/**
 * Column definitions for future Maintenance table views.
 * Maps field names to display labels.
 * @type {Object<string, string>}
 */
export const ALL_MAINTENANCE_COLUMNS = Object.freeze({
  item: "Item",
  linked_to: "Linked To",
  types: "Type",
  priority: "Urgency",
  status: "Status",
  managed_by: "Managed By",
  technician: "Technician",
  due_date: "Due Date",
  completed_on: "Completed On",
  cost: "Cost",
});

export const BOARD_TABLE_COLUMN_DEFINITIONS = Object.freeze({
  tasks: ALL_TASK_COLUMNS,
  patients: ALL_PATIENT_COLUMNS,
  projects: ALL_PROJECT_COLUMNS,
  assets: ALL_ASSET_COLUMNS,
  labs: ALL_LAB_COLUMNS,
  maintenance: ALL_MAINTENANCE_COLUMNS,
});

/**
 * Returns table column definitions for a parent-board view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {Object<string, string>} Column definitions, or an empty object.
 */
export function getBoardTableColumnDefinitions(viewKey) {
  return BOARD_TABLE_COLUMN_DEFINITIONS[viewKey] || {};
}
